import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.mermas-hosteleria-prueba-papelera.${field}`

const LEY_HREF = 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2025-6597'
const ELESPANOL_HREF =
    'https://www.elespanol.com/sociedad/20260509/espana-cambia-normas-bares-restaurantes-deberan-ofrecer-raciones-pequenas-entregar-sobras-gratis/1003744237215_0.html'

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        href: LEY_HREF,
        labelKey: 'sources.1',
        fallback:
            'Ley 1/2025, de 1 de abril, de prevención de las pérdidas y el desperdicio alimentario, BOE de 2 de abril de 2025, artículos 3, 6 y 8.',
    },
    {
        href: ELESPANOL_HREF,
        labelKey: 'sources.2',
        fallback:
            'El Español, nuevas obligaciones para bares y restaurantes por la Ley 1/2025, 9 de mayo de 2026.',
    },
    {
        labelKey: 'sources.3',
        fallback:
            'Lectura de la exención de microempresas extendida a toda la ley: boletín normativo de Primera Lectura Ediciones, abril de 2025.',
    },
    {
        labelKey: 'sources.4',
        fallback:
            'Caso de cliente: asesoría de Tipi, contado de forma anónima. Food cost e importes de la papelera son del cliente.',
    },
    {
        labelKey: 'sources.5',
        fallback:
            'Semana de ejemplo didáctica, rendimiento del solomillo y paso a euros: elaboración propia de Tipi.',
    },
]

export function MermasHosteleriaPruebaPapeleraArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'creencia', label: t(k('toc.1'), '1. «Más o menos sé lo que tiro»') },
        { id: 'que-es', label: t(k('toc.2'), '2. ¿Qué cuenta como merma?') },
        { id: 'papelera', label: t(k('toc.3'), '3. La prueba de la papelera') },
        { id: 'cubo-no-ve', label: t(k('toc.4'), '4. Lo que el cubo no ve') },
        { id: 'caso', label: t(k('toc.5'), '5. 400 € a la semana') },
        { id: 'ejemplo', label: t(k('toc.6'), '6. Una semana pasada a euros') },
        { id: 'solomillo', label: t(k('toc.7'), '7. El solomillo y la ficha') },
        { id: 'normal', label: t(k('toc.8'), '8. ¿Cuánta merma es normal?') },
        { id: 'acciones', label: t(k('toc.9'), '9. Qué haces con cada cubo') },
        { id: 'limites', label: t(k('toc.10'), '10. Hasta dónde llega una semana') },
    ]

    const weekRows = [
        { item: t(k('table.week.1'), 'Pulpo cocido pasado de fecha, 1,8 kg'), euros: t(k('table.week.1c'), '50,40 €') },
        {
            item: t(k('table.week.2'), 'Recortes de solomillo al limpiar, 1,3 kg'),
            euros: t(k('table.week.2c'), '33,80 €'),
        },
        { item: t(k('table.week.3'), 'Lo que volvió en los platos, unos 5 kg'), euros: t(k('table.week.3c'), '15,00 €') },
        { item: t(k('table.week.4'), 'Sobrante del menú, 6 raciones'), euros: t(k('table.week.4c'), '10,80 €') },
        { item: t(k('table.week.5'), 'Pan del día anterior, 4 kg'), euros: t(k('table.week.5c'), '9,60 €') },
        { item: t(k('table.week.6'), 'Verdura pasada en cámara, 3 kg'), euros: t(k('table.week.6c'), '6,00 €') },
        { item: t(k('table.week.7'), 'Mise en place caducada, 1,5 kg'), euros: t(k('table.week.7c'), '6,00 €') },
        { item: t(k('table.week.8'), 'Errores de pase, 4 raciones'), euros: t(k('table.week.8c'), '3,60 €') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Las mermas en hostelería son tres cosas distintas: lo que se va al limpiar el género, lo que se tira porque sobra o caduca, y lo que falta sin que nadie sepa por qué. Solo la tercera puede ser robo o error del proveedor, y no se ve hasta que mides las otras dos. La forma más barata de medirlas es una hoja y un rotulador pegados al cubo de cocina durante siete días: qué, cuánto y por qué. El domingo se pasa a euros con el precio del último albarán, sin IVA. En un ejemplo nuestro, un restaurante que vende 25.000 € al mes tira 135 € a la semana, y casi todo es dinero que su ficha no contaba.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Cubo de cocina de un restaurante con una hoja y un rotulador para apuntar mermas',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGap2xl, 'text-article-muted-soft')}>
                                {t(k('published'), 'Publicado el 15 de octubre de 2026.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='creencia' className={articleUi.h2}>
                        {t(k('h2.creencia'), '«Más o menos sé lo que tiro»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.1'),
                                'Lo ves cada noche. Sabes que el pan sobra los lunes y que el pescado del jueves, si no sale el viernes, va fuera.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.2'),
                                'Lo grande lo ves. Lo pequeño que se repite, no: los recortes de más, la salsa que se pasó, las raciones del menú que nadie pidió.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.creencia.3Strong'), 'Nadie sabe cuánto tira hasta que lo apunta.')}
                            </strong>{' '}
                            {t(k('p.creencia.3After'), 'El cubo se vacía cada noche y no deja rastro.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.4Before'),
                                'Un cliente nuestro no conseguía bajar su coste de materia prima. Primero ',
                            )}
                            <LearnArticleLink slug='como-hacer-inventario-hosteleria'>
                                {t(k('p.creencia.4Link'), 'hizo inventario')}
                            </LearnArticleLink>
                            {t(k('p.creencia.4Mid'), ' y su food cost pasó del ')}
                            <strong>{t(k('p.creencia.4Strong46'), '46 %')}</strong>
                            {t(k('p.creencia.4To'), ' al ')}
                            <strong>{t(k('p.creencia.4Strong42'), '42 %')}</strong>
                            {t(
                                k('p.creencia.4After'),
                                '. Cuando midió cuánto se iba a la basura, entendió el resto.',
                            )}
                        </p>
                    </div>

                    <h2 id='que-es' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.queEs'), '¿Qué cuenta como merma en un restaurante?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.queEs.1'),
                                'Merma es todo el género que compras y no llega a la mesa convertido en plato cobrado. Sale de tres sitios:',
                            )}
                        </p>
                        <DashList
                            items={[
                                t(
                                    k('list.queEs.1'),
                                    'Merma de rendimiento. Lo que se va al limpiar, despiezar o cocinar: la grasa del solomillo, la piel, el agua que suelta la carne. Está prevista y vive en la ficha del plato.',
                                ),
                                t(
                                    k('list.queEs.2'),
                                    'Merma operativa. Lo que se tira sin estar previsto: lo que caduca, lo que sobra, el plato que vuelve, la fritura que se quema en el pase.',
                                ),
                                t(
                                    k('list.queEs.3'),
                                    'Diferencia sin explicar. Lo que falta cuando restas las dos anteriores. Aquí entran las invitaciones sin apuntar, el albarán que no cuadra y, sí, el robo.',
                                ),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <strong>{t(k('p.queEs.2Strong'), 'El problema es el orden.')}</strong>{' '}
                            {t(
                                k('p.queEs.2After'),
                                'Si no mides las dos primeras, todo lo que falta cae en la tercera, y un rendimiento mal calculado parece que se lo lleva alguien.',
                            )}
                        </p>
                    </div>

                    <h2 id='papelera' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.papelera'), 'Cómo medir las mermas en hostelería: la prueba de la papelera')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.papelera.1'),
                                'Una hoja pegada en la tapa del cubo y un rotulador colgado de una cuerda. Siete días seguidos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.papelera.2'),
                                'Cada vez que algo va a la basura, quien lo tira apunta tres cosas:',
                            )}
                        </p>
                        <ol className={articleUi.numList}>
                            <li className={articleUi.numItem}>
                                <strong>{t(k('list.papelera.1Strong'), 'Qué es.')}</strong>{' '}
                                {t(k('list.papelera.1After'), '«Recortes de solomillo», no «carne».')}
                            </li>
                            <li className={articleUi.numItem}>
                                <strong>{t(k('list.papelera.2Strong'), 'Cuánto.')}</strong>{' '}
                                {t(
                                    k('list.papelera.2After'),
                                    'Pesado si hay báscula a mano, a ojo si no. Mejor un número aproximado que ninguno.',
                                )}
                            </li>
                            <li className={articleUi.numItem}>
                                <strong>{t(k('list.papelera.3Strong'), 'Por qué.')}</strong>{' '}
                                {t(
                                    k('list.papelera.3After'),
                                    'Limpieza, caducado, sobró, volvió del plato, se quemó.',
                                )}
                            </li>
                        </ol>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.papelera.3'),
                                'El domingo, cada línea por el precio del último albarán, sin IVA.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.papelera.4Before'), 'Antes de pegar la hoja, la pega: ')}
                            <strong>
                                {t(
                                    k('p.papelera.4Strong'),
                                    'la primera semana va a salir más de lo que crees, y tu equipo va a pensar que buscas culpables.',
                                )}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.papelera.5'),
                                'Díselo antes de empezar: se apunta qué, no quién. Si la hoja se convierte en una lista de acusados, a los tres días nadie la rellena.',
                            )}
                        </p>
                    </div>

                    <h2 id='cubo-no-ve' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cuboNoVe'), 'Lo que el cubo no ve nunca')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.cubo.1Strong'), 'Lo que sirves de más no llega a la basura.')}
                            </strong>{' '}
                            {t(
                                k('p.cubo.1After'),
                                'Si la ficha dice 200 gramos y en el pase salen 240, esos 40 gramos se los come el cliente y solo aparecen en el inventario, como género que falta. Igual que la caña al amigo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cubo.2Before'),
                                'Por eso la papelera no sustituye al conteo: lo completa. Si aún no sacas la diferencia entre lo que debería quedar en la cámara y lo que hay, ',
                            )}
                            <LearnArticleLink slug='como-hacer-inventario-hosteleria'>
                                {t(
                                    k('p.cubo.2Link'),
                                    'este método la saca contando solo las 20 o 30 referencias que pesan',
                                )}
                            </LearnArticleLink>
                            {t(k('p.cubo.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cubo.3'),
                                'Lo que sobre después de restar la hoja es lo único que merece una conversación incómoda.',
                            )}
                        </p>
                    </div>

                    <h2 id='caso' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.caso'), 'Del inventario a la papelera: 400 € a la semana')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.1Before'), 'Es el mismo cliente del ')}
                            <LearnArticleLink slug='como-hacer-inventario-hosteleria'>
                                {t(k('p.caso.1Link'), 'artículo de inventario')}
                            </LearnArticleLink>
                            {t(
                                k('p.caso.1After'),
                                ': el que pensaba que pedía de más y, al contar, vio que el food cost bajaba del 46 % al 42 %.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caso.2'),
                                'La diferencia seguía ahí. Así que pegaron la hoja al cubo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.3Before'), 'La primera semana salieron ')}
                            <strong>{t(k('p.caso.3Strong400'), '400 €')}</strong>
                            {t(k('p.caso.3Mid'), ' tirados. Al mes, unos ')}
                            <strong>{t(k('p.caso.3Strong1600'), '1.600 €')}</strong>
                            {t(k('p.caso.3After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caso.4Before'), 'Eso era el ')}
                            <strong>{t(k('p.caso.4Strong7'), '7 % de su facturación')}</strong>
                            {t(
                                k('p.caso.4After'),
                                ': dinero que salía por la basura y que ninguna ficha había contado del todo.',
                            )}
                        </p>
                        <div className={cn(articleUi.stats, 'mt-2')}>
                            <div className={articleUi.statAlert}>
                                <p className={articleUi.statLabelAlert}>
                                    {t(k('stat.week.label'), 'A la semana')}
                                </p>
                                <p className={articleUi.statValueAlert}>{t(k('stat.week.value'), '400 €')}</p>
                            </div>
                            <div className={articleUi.stat}>
                                <p className={articleUi.statLabel}>{t(k('stat.month.label'), 'Al mes')}</p>
                                <p className={articleUi.statValue}>{t(k('stat.month.value'), '1.600 €')}</p>
                            </div>
                            <div className={articleUi.statAccent}>
                                <p className={articleUi.statLabelAccent}>
                                    {t(k('stat.pct.label'), 'De la facturación')}
                                </p>
                                <p className={articleUi.statValueAccent}>{t(k('stat.pct.value'), '7 %')}</p>
                            </div>
                        </div>
                        <p className={cn(articleUi.pItalic, 'mt-2 mb-10')}>
                            {t(
                                k('p.caso.note'),
                                'Importes de la papelera y % de food cost del cliente, contados de forma anónima. La semana inventada de más abajo sirve solo para enseñar la cuenta línea a línea.',
                            )}
                        </p>
                    </div>

                    <h2 id='ejemplo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.ejemplo'), 'Una semana de cubo, pasada a euros (ejemplo didáctico)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd, 'italic text-article-muted')}>
                            {t(
                                k('p.ejemplo.lead'),
                                'Semana inventada para enseñar la cuenta: un restaurante con unos 25.000 € de venta mensual sin IVA y precios de albarán redondos. No es el caso del cliente de arriba.',
                            )}
                        </p>
                    </div>
                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.week.caption'), 'Semana de ejemplo pasada a euros')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.week.col.item'), 'Lo apuntado')}</th>
                                    <th scope='col'>{t(k('table.week.col.euros'), 'En euros')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {weekRows.map(row => (
                                    <tr key={row.item}>
                                        <td>{row.item}</td>
                                        <td className='tabular-nums'>{row.euros}</td>
                                    </tr>
                                ))}
                                <tr className={articleUi.tableTotal}>
                                    <th scope='row'>{t(k('table.week.total'), 'Total de la semana')}</th>
                                    <td className='tabular-nums'>
                                        <strong>{t(k('table.week.totalc'), '135,20 €')}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.ejemplo.1Strong'), 'Son unos 7.000 € al año.')}</strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.2Before'), 'La ficha solo contaba con ')}
                            <strong>{t(k('p.ejemplo.2Strong1560'), '15,60 €')}</strong>
                            {t(
                                k('p.ejemplo.2Mid'),
                                ' a la semana: los 600 gramos de recorte que preveía para el solomillo. El resto, unos ',
                            )}
                            <strong>{t(k('p.ejemplo.2Strong6200'), '6.200 € al año')}</strong>
                            {t(k('p.ejemplo.2After'), ', no estaba en ningún número.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.ejemplo.3Before'), 'Sobre 300.000 € de venta anual, eso son ')}
                            <strong>{t(k('p.ejemplo.3Strong'), 'dos puntos de food cost')}</strong>
                            {t(k('p.ejemplo.3After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <strong>{t(k('p.ejemplo.4Strong'), 'Un solo producto es el 37 % del cubo.')}</strong>{' '}
                            {t(
                                k('p.ejemplo.4After'),
                                'El pulpo no es un problema de cocina, es de compra: se pidió para un fin de semana que no llegó.',
                            )}
                        </p>
                    </div>

                    <h2 id='solomillo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.solomillo'), 'El solomillo no está en la basura, está en la ficha')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.solomillo.1'),
                                'La ficha decía que de 4 kg de solomillo se iban 600 gramos al limpiar, un 15 %. La hoja dice 1,3 kg, más del 32 %.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.solomillo.2Before'), 'A 26 €/kg, la ración de 200 gramos limpios no cuesta ')}
                            <strong>{t(k('p.solomillo.2Strong612'), '6,12 €')}</strong>
                            {t(k('p.solomillo.2Mid'), ', como dice la ficha. Cuesta ')}
                            <strong>{t(k('p.solomillo.2Strong770'), '7,70 €')}</strong>
                            {t(k('p.solomillo.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.solomillo.3'),
                                'En el inventario, ese dinero aparece como «falta solomillo», y es justo el que se le achaca al proveedor o a la plantilla. No falta nada: se queda en la tabla de cortar.',
                            )}
                        </p>
                    </div>

                    <h2 id='normal' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.normal'), '¿Cuánta merma es normal en un restaurante?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.normal.1'),
                                'No hemos encontrado ninguna cifra publicada y fiable para restaurantes en España. Desconfía de quien te dé un porcentaje universal.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <strong>{t(k('p.normal.2Strong'), 'Lo normal es lo que dice tu ficha.')}</strong>{' '}
                            {t(
                                k('p.normal.2After'),
                                'Si prevé un 15 % de limpieza y tu cocina saca un 32 %, el problema no es que tires mucho: es que la ficha no describe tu cocina.',
                            )}
                        </p>
                    </div>

                    <h2 id='acciones' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.acciones'), 'Qué haces con cada cubo')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        <li className={articleUi.numItem}>
                            <strong>{t(k('list.acciones.1Strong'), 'Rendimiento: corrige la ficha.')}</strong>{' '}
                            {t(
                                k('list.acciones.1Mid'),
                                'Elige los tres productos que más limpias. Pesa en bruto, limpia como se limpia en tu cocina y pesa lo útil. Tres veces, en días distintos, y te quedas con la media. Si el plato no tiene ficha, ',
                            )}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('list.acciones.1Link'), 'aquí está cómo montar el escandallo desde cero')}
                            </LearnArticleLink>
                            {t(k('list.acciones.1After'), '.')}
                        </li>
                        <li className={articleUi.numItem}>
                            <strong>
                                {t(k('list.acciones.2Strong'), 'Operativa: cambia el pedido o la producción.')}
                            </strong>{' '}
                            {t(
                                k('list.acciones.2After'),
                                'Si un producto se repite en la hoja, se compra o se produce de más. Menos cantidad, más veces.',
                            )}
                        </li>
                        <li className={articleUi.numItem}>
                            <strong>
                                {t(k('list.acciones.3Strong'), 'Lo que vuelve del plato: mira la ración.')}
                            </strong>{' '}
                            {t(
                                k('list.acciones.3After'),
                                'Si un plato vuelve a medias una y otra vez, o el gramaje es grande o el plato no gusta. Las dos cosas cuestan.',
                            )}
                        </li>
                        <li className={articleUi.numItem}>
                            <strong>{t(k('list.acciones.4Strong'), 'Lo que falta después: ahora sí, busca.')}</strong>{' '}
                            {t(
                                k('list.acciones.4After'),
                                'Primero el gramaje en el pase, después las invitaciones, después los albaranes. Lo último que miras son las personas.',
                            )}
                        </li>
                    </ol>

                    <h2 id='limites' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.limites'), 'Hasta dónde llega una semana de hoja')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.limites.1Strong'), 'Una semana no es un año.')}</strong>{' '}
                            {t(
                                k('p.limites.1After'),
                                'Octubre no se parece a agosto. Repite la prueba cuando cambies de carta o de temporada.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.limites.2Strong'), 'El peso a ojo engaña.')}</strong>{' '}
                            {t(
                                k('p.limites.2After'),
                                'Sin báscula, la hoja sirve para ver qué se repite, no para cuadrar al céntimo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.limites.3Strong'), 'La ley llama desperdicio a menos cosas que tú.')}
                            </strong>{' '}
                            {t(k('p.limites.3Before'), 'La ')}
                            <a href={LEY_HREF} target='_blank' rel='noopener noreferrer' className={articleUi.link}>
                                {t(
                                    k('p.limites.3Link'),
                                    'Ley 1/2025 de prevención de las pérdidas y el desperdicio alimentario',
                                )}
                            </a>{' '}
                            {t(
                                k('p.limites.3After'),
                                'lo define como «la parte de los alimentos destinada a ser ingerida por el ser humano y que termina desechada como residuo». La grasa del solomillo no entra; en tu cuenta de resultados, sí.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.limites.4Strong'), 'Y no te obliga a lo que te han contado.')}</strong>{' '}
                            <a
                                href={ELESPANOL_HREF}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={articleUi.link}
                            >
                                {t(k('p.limites.4Link'), 'Hay medios que han publicado')}
                            </a>{' '}
                            {t(
                                k('p.limites.4After'),
                                'que todos los bares deben tener un plan de prevención desde abril de 2026. El artículo 6 exceptúa los locales de hasta 1.300 m² de venta, salvo que los del mismo CIF sumen más, y añade que «las microempresas quedan excluidas de las obligaciones a las que se refieren los apartados anteriores del presente artículo».',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.5'),
                                'Lo que el artículo 8 sí pide a toda la hostelería es dejar que el cliente se lleve lo que no se ha comido sin cobrárselo, salvo el envase de plástico de un solo uso, y avisarlo en la carta. Algún boletín jurídico extiende aquí la exención de las microempresas; el BOE no lo dice.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.6'),
                                'Haz la prueba esta semana. Si el domingo te sale una cifra y no sabes de dónde viene la mitad, dínoslo y te decimos por dónde empezar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.limites.7'), 'Y cuando sumes la hoja, hazlo sentado.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿No sabes de dónde sale la mitad del cubo?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanos qué ha salido en tu hoja: te ayudamos a separar rendimiento, operativa y lo que falta de verdad.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                    <p className={cn(articleUi.ctaClose, 'mt-6')}>
                        {t(k('cta.closeBefore'), 'Desde ')}
                        <TipiLink />
                        {t(
                            k('cta.closeAfter'),
                            ' te ayudamos a ver la merma en euros, no solo en porcentaje.',
                        )}
                    </p>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={articleUi.link}
                                    >
                                        {t(k(source.labelKey), source.fallback)}
                                    </a>
                                ) : (
                                    t(k(source.labelKey), source.fallback)
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
